import { useCallback, useEffect, useState } from 'react';
import { ClickAwayOptions, ScrollAwayOptions } from '@optimus-web/helpers';

export interface OpenableOverlayHookProps {
    registerClickAway: (options: ClickAwayOptions) => void;
    deregisterClickAway: () => void;
    registerScrollAway: (options: ScrollAwayOptions) => void;
    deregisterScrollAway: () => void;
}

export const useOpenableOverlayHook = ({
    deregisterClickAway,
    deregisterScrollAway,
    registerClickAway,
    registerScrollAway,
}: OpenableOverlayHookProps) => {
    // state of openable overlay -> main hook
    const [opened, setOpened] = useState(false);

    // overlay ref
    let overlayElement: HTMLElement | null = null;
    const handlerOverlayRef = (c: HTMLElement | null): void => {
        overlayElement = c;
    };

    let anchorElement: HTMLElement | null | undefined;
    const handleAnchorRef = (c: HTMLElement | null): void => {
        anchorElement = c;
    };

    const getAnchorRef = () => anchorElement;

    const open = () => {
        if (!opened) {
            setOpened(true);
        }
    };

    const close = useCallback(() => {
        if (opened) {
            setOpened(false);
        }
    }, [opened]);

    // componenDidUpdate -> subscribes on update if opened -> return function unsubscribes if closed
    useEffect(() => {
        if (opened) {
            // TODO: In the future parametrize options and spread this hook
            const options = {
                handler: close,
                roots: [anchorElement, () => overlayElement],
            };
            if (registerClickAway) {
                registerClickAway(options);
            }
            if (registerScrollAway) {
                registerScrollAway(options);
            }
            return () => {
                if (deregisterClickAway) {
                    deregisterClickAway();
                }
                if (deregisterScrollAway) {
                    deregisterScrollAway();
                }
            };
        }
    }, [
        opened,
        anchorElement,
        close,
        deregisterClickAway,
        overlayElement,
        deregisterScrollAway,
        registerClickAway,
        registerScrollAway,
    ]);

    const handleIconClick = () => (opened ? close() : open());

    return {
        opened,
        close,
        handlerOverlayRef,
        anchorElement,
        handleAnchorRef,
        handleIconClick,
        getAnchorRef,
    };
};
